/* import __COLOCATED_TEMPLATE__ from './custom-emoji.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
// @ts-ignore
import { cached } from 'tracked-toolbox';
import {
  type EmojiItem,
  getEmojiItemList,
  wrapInColons,
  hasNativeEmojiSupport,
  applySkinTone,
} from '@intercom/composer-ember/lib/helpers/emoji';
import type { TypeaheadMatchResult } from '@intercom/composer-ember';
import type Composer from '@intercom/composer-ember/lib/composer';
import type CustomEmojiService from 'embercom/services/custom-emoji-service';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';
import { type SafeString } from '@ember/template/-private/handlebars';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import type Session from 'embercom/services/session';

interface Args {
  composer: Composer;
  matchResult: TypeaheadMatchResult | null;
  registerDelegate(f: () => void): void;
  deregisterDelegate(f: () => void): void;
}

// Extended EmojiItem type that can handle custom emojis
interface ExtendedEmojiItem extends EmojiItem {
  isCustom?: boolean;
  customEmojiData?: CustomEmoji;
  display: SafeString; // Override to ensure SafeString type
}

export default class CustomEmojiInserter extends Component<Args> {
  @service declare customEmojiService: CustomEmojiService;
  @service declare session: Session;
  @service declare intercomEventService: $TSFixMe;

  emojiSize = 16;
  emojiImageClass = 'emoji__image';

  get skinToneModifier() {
    return this.args.composer.config.skinToneModifier;
  }

  @cached
  get standardEmojiItems(): Array<EmojiItem> {
    return getEmojiItemList(this.skinToneModifier, true);
  }

  @cached
  get customEmojiItems(): Array<ExtendedEmojiItem> {
    if (!this.session.workspace.isFeatureEnabled('team-messenger-custom-emojis')) {
      return [];
    }

    return this.customEmojiService.getAllCustomEmojis().map((customEmoji) => ({
      identifier: customEmoji.identifier,
      unicodeIcon: '', // Custom emojis don't have unicode
      display: sanitizeHtml(
        `<img src="${customEmoji.src}" alt="${customEmoji.alt}" class="custom-emoji" />`,
      ), // Use image tag for display
      isCustom: true,
      customEmojiData: customEmoji,
    }));
  }

  @cached
  get allEmojiItems(): Array<ExtendedEmojiItem> {
    // Combine custom emojis with standard emojis, custom emojis first
    return [...this.customEmojiItems, ...this.standardEmojiItems];
  }

  @cached
  get filteredEmoji(): Array<ExtendedEmojiItem> {
    if (!this.args.matchResult) {
      return [];
    }
    if (this.args.matchResult.text.length === 1) {
      return this.allEmojiItems;
    }
    let matchWithoutTrigger = this.args.matchResult.text.replace(':', '');
    let escapedMatch = this.escapeRegex(matchWithoutTrigger);
    let regexp = new RegExp(`^${escapedMatch}`, 'i');
    return this.allEmojiItems.filter((item: ExtendedEmojiItem) => regexp.test(item.identifier));
  }

  private escapeRegex(string: string) {
    return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
  }

  @action
  insertEmoji(item: ExtendedEmojiItem): void {
    if (this.args.matchResult) {
      if (item.isCustom && item.customEmojiData) {
        this.trackCustomEmojiInsertion(item.customEmojiData);
        // Insert custom emoji as inline image
        this.insertCustomEmoji(item.customEmojiData);
      } else {
        // Insert standard emoji as text
        let text = `${
          hasNativeEmojiSupport()
            ? applySkinTone(item.unicodeIcon, this.skinToneModifier)
            : wrapInColons(item.identifier)
        } `;

        this.args.composer.commands.insertTextAtRange(this.args.matchResult?.range, text);
      }
      this.args.composer.commands.focus();
    }
  }

  private trackCustomEmojiInsertion(customEmoji: CustomEmoji): void {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'custom_emoji',
      section: 'composer',
      emoji_identifier: customEmoji.identifier,
      emoji_type: 'custom',
      insertion_method: 'typeahead',
    });
  }

  private insertCustomEmoji(customEmoji: CustomEmoji): void {
    if (this.args.matchResult) {
      let state = this.args.composer.state.prosemirrorState;

      // Create inline image node
      let inlineImageNode = state.schema.nodes.inlineImage.create({
        src: customEmoji.src,
        text: customEmoji.alt || customEmoji.identifier,
        type: 'inlineImage',
        className: 'custom-emoji',
      });

      // Create text node for the space
      let spaceNode = state.schema.text(' ');

      // Replace the matched text with the inline image and space
      let tr = state.tr.replaceWith(
        this.args.matchResult.range.from,
        this.args.matchResult.range.to,
        [inlineImageNode, spaceNode],
      );
      this.args.composer.commands.dispatch(tr);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Tools::Typeaheads::CustomEmoji': typeof CustomEmojiInserter;
    'tools/typeaheads/custom-emoji': typeof CustomEmojiInserter;
  }
}
